// The "locked" treatment, in one place. Once a campaign leaves draft its
// revision-aware plan is immutable, and a creative used by such a campaign is
// immutable with it; every surface that has to say so renders one of these.
//
// Lived inside the campaign builder until the creative library needed the
// banner too. The builder wraps a locked section's body in `pointer-events:
// none`, so a banner that carries an action must be rendered *outside* that
// body (`Section`'s `lockNotice`), which is why `LockBanner` takes a
// `className` — `.locked-banner--section` keeps its geometry when it is no
// longer the first child of `.sec-body`, `.locked-banner--inset` boxes it
// inside a panel.

import type { ReactNode } from 'react';

export interface LockGlyphProps {
  size?: number;
}

export const LockGlyph = ({ size = 13 }: LockGlyphProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="7.5" width="10" height="6" rx="1" />
    <path d="M5.5 7.5V5a2.5 2.5 0 0 1 5 0v2.5" />
  </svg>
);

export interface LockedFieldProps {
  label: ReactNode;
  value: ReactNode;
  sublabel?: ReactNode;
  reason?: ReactNode;
}

export function LockedField({ label, value, sublabel, reason }: LockedFieldProps) {
  return (
    <div className="locked-field" role="status">
      <span className="lf-ico" title="Locked after publishing handoff">
        <LockGlyph />
      </span>
      <div className="lf-body">
        <div className="lf-label">{label} · LOCKED</div>
        <div className="lf-value">
          <span>{value}</span>
          {sublabel && <span className="lf-sub">{sublabel}</span>}
        </div>
        {reason && <div className="lf-reason">{reason}</div>}
      </div>
    </div>
  );
}

/** A second thing the operator can do from the banner, beside Duplicate. */
export interface LockBannerAction {
  label: ReactNode;
  onClick: () => void;
}

export interface LockBannerProps {
  children?: ReactNode;
  /** Renders the action button; without it the banner only explains. */
  onDuplicate?: () => void;
  /**
   * Rendered before Duplicate. A locked plan is not always a dead end: the Schedule
   * section of a live or paused campaign offers Extend flight here (2026-10-21).
   */
  action?: LockBannerAction;
  title?: ReactNode;
  actionLabel?: ReactNode;
  inset?: boolean;
  className?: string;
}

export function LockBanner({
  children,
  onDuplicate = undefined,
  action = undefined,
  title = 'Locked after publishing handoff',
  actionLabel = 'Duplicate to change',
  inset = false,
  className = '',
}: LockBannerProps) {
  const classes = ['locked-banner', inset ? 'locked-banner--inset' : '', className]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={classes} role="status">
      <span className="lb-ico">
        <LockGlyph size={11} />
      </span>
      <div className="lb-body">
        <div className="lb-title">{title}</div>
        <div className="lb-sub">{children}</div>
      </div>
      {(action || onDuplicate) && (
        <div className="lb-action">
          {action && (
            <button type="button" className="btn btn-sm" onClick={action.onClick}>
              {action.label}
            </button>
          )}
          {onDuplicate && (
            <button type="button" className="btn btn-sm" onClick={onDuplicate}>
              {actionLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Subtle inline hint while still in draft — teaches the user what locks
// after they hit Launch.
export interface LockHintProps {
  children?: ReactNode;
}

export function LockHint({ children = 'Locks when ready' }: LockHintProps) {
  return (
    <span
      className="lock-hint"
      title="This setting becomes permanent once the campaign is marked ready to publish."
    >
      <span className="lh-ico">
        <LockGlyph size={10} />
      </span>
      {children}
    </span>
  );
}
